import { color, rgb } from '@visx/vendor/d3-color';
import { interpolateHcl, piecewise } from '@visx/vendor/d3-interpolate';
import { mapValues, shuffle, startCase } from 'lodash';

const vendorColors = {
    token: [
        {
            slug: 'eth',
            hex: '#637EEA',
        },
        {
            slug: 'avax',
            hex: '#E84143',
        },
        {
            slug: 'polygon',
            hex: '#8246E5',
        },
        {
            slug: 'sol',
            hex: '#D62FF5',
        },
        {
            slug: 'btc',
            hex: '#EF8E19',
        },
        {
            slug: 'bnb',
            hex: '#F1B90C',
        },
    ],
};

const chartColors = {
    indigo: '#383D9F',
    blue: '#545CF8',
    violet: '#904EA6',
    red: '#F0384A',
    orange: '#F79009',
    green: '#63B512',
    aqua: '#5ECCA0',
};

const brandColors = [
    '#5B35D5',
    '#779BE7',
    '#8F7BE1',
    '#EF798A',
    '#C0B9D8',
    '#EFEEF0',
    '#887F8F',
    '#313335',
    '#001F22',
    '#F3A712',
];

const createMonochromePalette = (color: string) => {
    const baseColor = rgb(color);
    if (!baseColor) return [];
    return [
        baseColor.darker(1.5),
        baseColor.darker(0.75),
        baseColor,
        baseColor.brighter(0.75),
        baseColor.brighter(1.5),
    ].map(color => color.formatRgb());
};

const dynamicPalettes = {
    brand: brandColors,
    random: [
        chartColors.green,
        chartColors.aqua,
        chartColors.blue,
        chartColors.indigo,
        chartColors.violet,
        chartColors.red,
        chartColors.orange,
    ],
    ...mapValues(chartColors, createMonochromePalette),
};

const paletteOptions = Object.keys(dynamicPalettes).map(palette => ({ label: startCase(palette), value: palette }));

const interpolatePalette = (number: number, palette: keyof typeof dynamicPalettes = 'random') => {
    if (palette === 'brand') {
        return piecewise(interpolateHcl, dynamicPalettes[palette])(number);
    }
    if (palette === 'random') {
        const stops = shuffle(dynamicPalettes[palette].flatMap(color => createMonochromePalette(color)));

        return piecewise(interpolateHcl, stops)(number / stops.length);
    }
    const stops = dynamicPalettes[palette];
    return piecewise(interpolateHcl, stops)(number);
};

const getVendorColor = (column: string) => {
    for (const token of vendorColors.token) {
        const maybeToken = column.includes(token.slug);
        if (maybeToken) {
            return token.hex;
        }
    }
    return undefined;
};

const getRandomColor = (column: string) => {
    const vendorColor = getVendorColor(column);
    return vendorColor ? vendorColor : interpolatePalette(0, 'random');
};

const getContrastColor = (bgColor: string): '#000' | '#fff' => {
    const colorTools = color(bgColor);
    if (!colorTools) {
        console.error('Invalid color', bgColor);
        return '#000';
    }

    const rgb = colorTools.rgb();
    // Calculate luminance using D3's color utilities
    const luminance = 0.2126 * (rgb.r / 255) + 0.7152 * (rgb.g / 255) + 0.0722 * (rgb.b / 255);

    // Return black or white based on luminance
    return luminance > 0.5 ? '#000' : '#fff';
};

export { getContrastColor, getRandomColor, getVendorColor, interpolatePalette, paletteOptions, vendorColors };
